import React, { useMemo, useRef, useState } from 'react';
import { Modal } from '@bedrock/components';
import { ProTablePlus } from '@bedrock/eed-react';
import { useMemoizedFn } from 'ahooks';
import _ from 'lodash';
import withFunctionCall from '@mpc/common/src/utils/withFunctionCall';

// ============ 类型定义 ============

/**
 * AddItemsModal.show() 入参（不区分 single/multi 的公共字段）。
 *
 * **设计原则**：
 * - 顶层只保留 Modal 语义 + selection-matching 逻辑，≤8 个 prop
 * - ProTablePlus 的全部属性通过 `proTableProps` 唯一透传口扩展
 * - `selectMode` 通过判别联合联动 `onConfirm` 签名：单选回调单对象，多选回调数组
 */

export interface AddItemsModalShowPropsBase<TRecord = any, TItem = any> {
  // ===== Modal 层 =====
  /** 弹窗标题，默认 '请选择' */
  title?: string;
  /** 弹窗宽度，默认 960 */
  width?: number;
  /** 弹窗 className */
  className?: string;

  // ===== Selection-matching =====
  /** Table rowKey，用于 selectedRowKeys / disabledRowKeys + extractKey 默认值，默认 'id' */
  rowKey?: string;
  /**
   * 从 item 中提取唯一 key。
   * 当已选项（items）和 ProTablePlus 返回记录使用不同字段时（如 property_id vs id），
   * 通过此回调统一提取。不传时回退到 `item[rowKey]`（默认 'id'）
   */
  getItemKey?: (item: TItem | TRecord) => React.Key;
  /**
   * 已选项列表（来自外部 state），用于：
   * 1. disabledRowKeys（禁止取消已选项勾选）
   * 2. handleOk 时与表格新选项合并去重（unionBy key）
   * 3. maxLength 达到前，已选项不计入名额
   */
  items?: TItem[];
  /**
   * 最大可选数量（仅 multi 模式生效）。
   * 达到后未选中行自动 disabled，已选中行仍可取消勾选
   */
  maxLength?: number;
  /**
   * 将 ProTablePlus 返回的原始 record 映射为用户业务 item 格式。
   * 若不传，则直接使用原始 record
   */
  mapSelectedItems?: (selectedRecords: TRecord[]) => TItem[];

  // ===== ProTablePlus 唯一扩展口 =====
  /**
   * ProTablePlus 全部属性的透传容器。
   *
   * AddItemsModal 内部仅覆盖以下字段（其余全部透传）：
   * - `rowKey`：取自顶层 `rowKey` prop
   * - `rowSelection`：内部计算（type / selectedRowKeys / disabledRowKeys / getCheckboxProps），
   *   但 proTableProps.rowSelection 中的额外配置（如 props.fixed / columnTitle）会被保留合并
   * - `onSelectChange`：内部接管，无论 multi/single 均通过此回调驱动
   * - `onRow`：single 模式下内部接管 onClick 做行点击选中，但会先调用 proTableProps.onRow 再合并
   *
   * 调用方须至少提供：
   * - `proTableProps.columns`：列定义（推荐含 searchRender 列级筛选）
   * - `proTableProps.request.queryService`：数据拉取的 ProTablePlus 标准 queryService
   * - `proTableProps.toolbar.search`：顶部搜索框配置（id + placeholder）
   * - `proTableProps.pagination`：分页配置
   */
  proTableProps?: any;
}

/** Multi 模式：onConfirm 给数组 */
export interface AddItemsModalShowPropsMulti<TRecord = any, TItem = any>
  extends AddItemsModalShowPropsBase<TRecord, TItem> {
  selectMode?: 'multiple';
  onConfirm?: (items: TItem[]) => void;
}

/** Single 模式：onConfirm 给单对象 */
export interface AddItemsModalShowPropsSingle<TRecord = any, TItem = any>
  extends AddItemsModalShowPropsBase<TRecord, TItem> {
  selectMode: 'single';
  onConfirm?: (item: TItem) => void;
}

export type AddItemsModalShowProps<TRecord = any, TItem = any> =
  | AddItemsModalShowPropsMulti<TRecord, TItem>
  | AddItemsModalShowPropsSingle<TRecord, TItem>;

// ============ 内部组件 ============

const AddItemsModalInner = ({
  // Modal
  title = '请选择',
  width = 960,
  className,
  // Selection
  rowKey: propRowKey = 'id',
  selectMode = 'multiple',
  items: initialItems = [],
  maxLength,
  getItemKey,
  mapSelectedItems,
  // Callback
  onConfirm,
  // ProTablePlus
  proTableProps = {} as any,
  // withFunctionCall 注入：visible / onCancel(=destroy) / onClose(=destroy)
  ...rest
}: AddItemsModalShowPropsBase & Record<string, any>) => {
  const isSingle = selectMode === 'single';
  const tableRef = useRef<any>(null);

  // ---- Key 提取 ----
  const extractKey = useMemoizedFn((item: any) => {
    if (getItemKey) return getItemKey(item);
    return item?.[propRowKey];
  });

  // ---- 选中状态（命令式弹窗，每次 show 创建新实例） ----
  /** Multi 模式：表格新勾选的项（不含 items），与 items 合并后为最终 checkList */
  const [proSelectedList, setProSelectedList] = useState<any[]>([]);
  /** Single 模式：当前选中的行 */
  const [selectedItem, setSelectedItem] = useState<any>(null);

  // ---- 派生：rowSelection 参数 ----
  const disabledRowKeys = useMemo(
    () => initialItems.map((item: any) => extractKey(item)),
    [initialItems, extractKey],
  );

  const checkList = useMemo(
    () => (isSingle ? [] : [...initialItems, ...proSelectedList]),
    [isSingle, initialItems, proSelectedList],
  );

  const selectedRowKeys = useMemo(() => {
    if (isSingle) {
      return selectedItem ? [extractKey(selectedItem)] : [];
    }
    return checkList.map((item: any) => extractKey(item));
  }, [isSingle, selectedItem, checkList, extractKey]);

  // ---- maxLength 逻辑（仅 multi） ----
  const isMaxReached = useMemoizedFn(() => {
    return !isSingle && maxLength !== undefined && checkList.length >= maxLength;
  });

  const getCheckboxProps = useMemoizedFn((record: any) => {
    if (isMaxReached()) {
      const key = extractKey(record);
      if (!checkList.some((item: any) => extractKey(item) === key)) {
        return { disabled: true };
      }
    }
    return {};
  });

  // ---- 选中变更（ProTablePlus onSelectChange 统一回调） ----
  const handleSelectChange = useMemoizedFn((selectedRows: any[]) => {
    if (isSingle) {
      const record = selectedRows[0];
      if (!record) {
        setSelectedItem(null);
        return;
      }
      const mapped = mapSelectedItems
        ? mapSelectedItems([record])[0]
        : record;
      setSelectedItem(mapped);
    } else {
      // 过滤掉 items 中的项（它们由外部管理，不在表格勾选里重复计算）
      const filtered = selectedRows.filter(
        (row: any) =>
          !initialItems.some(
            (item: any) => String(extractKey(item)) === String(extractKey(row)),
          ),
      );
      const mapped = mapSelectedItems ? mapSelectedItems(filtered) : filtered;
      setProSelectedList(mapped);
    }
  });

  // ---- 确认回调 ----
  const handleOk = useMemoizedFn(() => {
    if (isSingle) {
      (onConfirm as any)?.(selectedItem);
    } else {
      const merged = _.unionBy(checkList, (item: any) => extractKey(item));
      (onConfirm as any)?.(merged);
    }
    rest?.onCancel?.();
  });

  // ---- onRow 合并（single 模式：行点击即选中） ----
  const mergedOnRow = useMemoizedFn((record: any) => {
    const baseProps = proTableProps?.onRow?.(record) || {};
    if (!isSingle) return baseProps;

    return {
      ...baseProps,
      onClick: (e: any) => {
        const mapped = mapSelectedItems
          ? mapSelectedItems([record])[0]
          : record;
        setSelectedItem(mapped);
        baseProps.onClick?.(e);
      },
    };
  });

  // ---- rowSelection 合并：内部核心字段覆盖，调用方额外字段保留 ----
  const mergedRowSelection = useMemo(() => {
    const callerRS = proTableProps?.rowSelection || {};
    const internal: any = {
      type: isSingle ? 'radio' : 'checkbox',
      selectedRowKeys,
      disabledRowKeys,
    };
    if (!isSingle) {
      internal.getCheckboxProps = getCheckboxProps;
      // 有 maxLength 限制时隐藏全选按钮（全选会绕过 getCheckboxProps 的逐行限制）
      if (maxLength !== undefined) {
        internal.hideSelectAll = true;
      }
    }
    return { ...callerRS, ...internal };
  }, [
    isSingle,
    selectedRowKeys,
    disabledRowKeys,
    getCheckboxProps,
    maxLength,
    proTableProps?.rowSelection,
  ]);

  // ============ 渲染 ============
  return (
    <Modal
      {...rest}
      title={title}
      width={width}
      className={className}
      onOk={handleOk}
      onCancel={() => rest?.onCancel?.()}
      maskClosable={false}
      destroyOnClose>
      <div className="">
        <ProTablePlus
          {...proTableProps}
          ref={(r: any) => {
            tableRef.current = r;
          }}
          rowKey={propRowKey}
          rowSelection={mergedRowSelection}
          onSelectChange={handleSelectChange}
          onRow={mergedOnRow}
        />
      </div>
    </Modal>
  );
};

AddItemsModalInner.displayName = 'AddItemsModal';

// ============ 导出：withFunctionCall 包装，暴露 .show() 命令式调用 ============

const AddItemsModalRaw = withFunctionCall(AddItemsModalInner);

/**
 * 命令式打开"搜索 + 分页 + 选择"弹窗。
 * 底层使用 ProTablePlus，所有表格/搜索/分页相关 props 通过 `proTableProps` 透传。
 *
 * @example Multi（多选）
 * ```ts
 * AddItemsModal.show({
 *   title: '选择属性',
 *   items: existingFields,          // 已选项（禁用 + 合并去重依据）
 *   maxLength: 5,                   // 多选上限
 *   getItemKey: (item) => item.id ?? item.property_id,
 *   mapSelectedItems: (records) => records.map(r => ({ ... })),
 *   onConfirm: (items: FieldDef[]) => { ... },  // items 是数组
 *   proTableProps: {
 *     columns: PROPERTY_COLUMNS,
 *     request: { queryService },
 *     toolbar: { search: { id: 'search_text', placeholder: '搜索' } },
 *     pagination: { pageSize: 10 },
 *   },
 * });
 * ```
 *
 * @example Single（单选）
 * ```ts
 * AddItemsModal.show({
 *   selectMode: 'single',
 *   title: '选择一项',
 *   onConfirm: (item: Record) => { ... },      // item 是单对象
 *   proTableProps: { ... },
 * });
 * ```
 */
export const AddItemsModal = {
  show<TRecord = any, TItem = any>(
    config: AddItemsModalShowProps<TRecord, TItem>,
  ) {
    return AddItemsModalRaw.show(config as any);
  },
};

export default AddItemsModal;
